import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { getTeacherStudents, Avatar } from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';
import AvatarDisplay from '../shared/AvatarDisplay';
import { StudentPracticeProgressDialog } from './StudentPracticeProgressDialog';

interface Student {
  user_id: string;
  name: string;
  email: string;
  school_id: string;
  avatar?: Avatar;
}

/**
 * MyStudents component for teachers to view their assigned students
 */
const MyStudents: React.FC = () => {
  const { user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressStudent, setProgressStudent] = useState<Student | null>(null);

  useEffect(() => {
    if (user) {
      loadStudents();
    }
  }, [user]);

  const loadStudents = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Use a placeholder ID - the backend will use currentUser from auth context
      // The teacher_id in the URL doesn't matter for "my students" view
      // because the backend uses the authenticated user's ID
      const response = await getTeacherStudents('me');
      setStudents(response.students);
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  const viewProgress = (student: Student) => {
    setProgressStudent(student);
    setProgressOpen(true);
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">My Students</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadStudents} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : students.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="text.secondary">
                No students assigned yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Contact your school administrator to assign students to you
              </Typography>
            </Box>
          ) : (
            <>
              <Box mb={2}>
                <Chip
                  label={`${students.length} student${students.length !== 1 ? 's' : ''} assigned`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Avatar</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.user_id} hover>
                        <TableCell>
                          <AvatarDisplay
                            avatar={student.avatar}
                            name={student.name}
                            role="student"
                            size="small"
                            showTooltip={false}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {student.name}
                          </Typography>
                        </TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="View practice progress">
                            <IconButton
                              size="small"
                              onClick={() => viewProgress(student)}
                              color="primary"
                              aria-label={`View ${student.name}'s practice progress`}
                            >
                              <AssessmentIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Email">
                            <IconButton
                              size="small"
                              onClick={() => handleEmailClick(student.email)}
                              color="primary"
                            >
                              <EmailIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
      <StudentPracticeProgressDialog
        open={progressOpen}
        studentId={progressStudent?.user_id || null}
        studentName={progressStudent?.name}
        onClose={() => setProgressOpen(false)}
      />
    </Box>
  );
};

export default MyStudents;
